import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { formUtils } from '@insurancemenu/imenu-components-lib';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { Cart } from 'types/cartServiceV2';
import { BrowsingInfo, CartBrowsingCriteria } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { cachedRequestFormById } from 'api/operations/formService';

const INITIAL_FORM_OBJECT: Readonly<FormObjectFromJSON> = Object.freeze({
  id: '',
  formTitle: '',
  sections: [],
  description: '',
});

const INITIAL_BC_OBJECT: Readonly<CartBrowsingCriteria> = Object.freeze({
  effectiveDate: '',
  zipCode: '',
  state: '',
  county: '',
  shortName: {},
});

export default class BrowsingCriteriaModalStore {
  rootStore: RootStore;
  cartId: string;
  form: FormObjectFromJSON;
  dynamicForm: null | formUtils.DynamicFormController;
  loading = false;
  showBrowsingCriteriaModal = false;
  productLineId: ProductLineId = 'GroupDental';
  browsingCriteria: CartBrowsingCriteria;

  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  async openBrowsingCriteriaModal(cart: Cart, productLineId: ProductLineId, formId: string): Promise<void> {
    this.setLoading(true);
    this.showBrowsingCriteriaModal = true;
    this.productLineId = productLineId;
    this.cartId = cart.id;
    this.dynamicForm = null;
    this.form = INITIAL_FORM_OBJECT;

    const { effectiveDate, zipCode, state, county, naics, sic2, sic4, groupSize } = cart.lastUpdateBrowsingCriteria;
    const convertedCartBc = {
      [productLineId]: [
        {
          effectiveDate,
          zipCode,
          state,
          county,
          naics,
          sic2,
          sic4,
          groupSize,
          // Product line specific information should have higher priority than the default one
          ...cart.lastUpdateBrowsingCriteria[productLineId],
        },
      ],
    };

    const res = await cachedRequestFormById(formId, convertedCartBc);
    runInAction(() => {
      this.form = res.data.getFormById;
      this.browsingCriteria = cart.lastUpdateBrowsingCriteria;
      const productLineAnswers = cart.lastUpdateBrowsingCriteria[productLineId];
      this.createFormController(productLineAnswers);
      this.setLoading(false);
    });
  }

  closeBrowsingCriteriaModal(): void {
    this.showBrowsingCriteriaModal = false;
  }

  createFormController(answers?: Record<string, string>): void {
    this.dynamicForm = new formUtils.DynamicFormController(this.form, this.rootStore.userStore.userRoles);
    if (answers) {
      this.dynamicForm.populateFields(answers);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async saveBrowsingCriteria(): Promise<Cart> {
    this.setLoading(true);
    if (!this.browsingCriteria[this.productLineId]) {
      this.browsingCriteria[this.productLineId] = {};
    }

    Object.entries(this.dynamicForm!.getCanonicalAnswers()).forEach(([key, value]) => {
      (this.browsingCriteria[this.productLineId] as BrowsingInfo)[key] = value?.toString() || '';
    });
    const res = await this.rootStore.cartStoreV2.updateBC(this.cartId, this.browsingCriteria);
    if (!res.data) {
      throw new Error(`Updating browsing criteria for ${this.cartId} wasn't successful`);
    }
    this.setLoading(false);
    return res.data.updateLastBC;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.cartId = '';
    this.dynamicForm = null;
    this.form = INITIAL_FORM_OBJECT;
    this.browsingCriteria = INITIAL_BC_OBJECT;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
